import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { TOURS, type TourId } from '../../help/tours';
import { useAuth } from '../../store/auth';
import { useTutorial } from '../../store/tutorial';

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PAD = 6;
const CARD_W = 340;

function targetRect(target: string | undefined): Rect | null {
  if (!target) return null;
  const el = document.querySelector<HTMLElement>(`[data-tour="${target}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 };
}

/** Posição do cartão: abaixo do alvo, ou acima, ou ao lado; sempre dentro da tela. */
function cardPosition(rect: Rect | null, cardH: number): { top: number; left: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const w = Math.min(CARD_W, vw - 24);
  if (!rect) return { top: Math.max(12, (vh - cardH) / 2), left: (vw - w) / 2 };
  const clampX = (x: number) => Math.min(Math.max(12, x), vw - w - 12);
  const clampY = (y: number) => Math.min(Math.max(12, y), vh - cardH - 12);
  if (rect.top + rect.height + 12 + cardH < vh)
    return { top: rect.top + rect.height + 12, left: clampX(rect.left) };
  if (rect.top - 12 - cardH > 0) return { top: rect.top - 12 - cardH, left: clampX(rect.left) };
  if (rect.left + rect.width + 12 + w < vw)
    return { top: clampY(rect.top), left: rect.left + rect.width + 12 };
  if (rect.left - 12 - w > 0) return { top: clampY(rect.top), left: rect.left - 12 - w };
  return { top: clampY(vh - cardH - 12), left: clampX(rect.left) };
}

/**
 * Tutorial da tela: aparece na primeira vez (por usuário e por navegador), destacando cada parte.
 * `after` espera outro tutorial ser concluído (ex.: o da partida antes do da tela).
 */
export function Tour({ id, after }: { id: TourId; after?: TourId }) {
  const user = useAuth((s) => s.user);
  const { enabled, seen, replaying, markSeen, stopReplay, setEnabled } = useTutorial();
  const key = `${user?.id ?? 'anon'}:${id}`;
  const prerequisite = after ? !!seen[`${user?.id ?? 'anon'}:${after}`] : true;
  const active = replaying === id || (enabled && !!user && !seen[key] && prerequisite && replaying === null);
  const steps = TOURS[id];
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const [cardH, setCardH] = useState(180);
  const [ready, setReady] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const step = steps[Math.min(index, steps.length - 1)]!;

  const close = useCallback(() => {
    markSeen(key);
    if (replaying === id) stopReplay();
    setIndex(0);
  }, [id, key, markSeen, replaying, stopReplay]);

  // Recomeça do primeiro passo quando é aberto de novo.
  useEffect(() => {
    if (active) setIndex(0);
  }, [active]);

  // Espera a tela montar antes de procurar o alvo; acompanha rolagem e redimensionamento.
  useEffect(() => {
    if (!active) {
      setReady(false);
      return;
    }
    const el = step.target ? document.querySelector<HTMLElement>(`[data-tour="${step.target}"]`) : null;
    el?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    const update = () => setRect(targetRect(step.target));
    const timer = window.setTimeout(() => {
      update();
      setReady(true);
    }, 120);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [active, step.target]);

  useLayoutEffect(() => {
    if (cardRef.current) setCardH(cardRef.current.offsetHeight);
  }, [index, ready, rect]);

  useEffect(() => {
    if (active && ready) cardRef.current?.focus();
  }, [active, ready, index]);

  if (!active || !ready) return null;
  const last = index === steps.length - 1;
  const pos = cardPosition(rect, cardH);
  const titleId = `tour-${id}-title`;

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') close();
    else if (e.key === 'ArrowRight' && !last) setIndex(index + 1);
    else if (e.key === 'ArrowLeft' && index > 0) setIndex(index - 1);
  };

  return (
    <div className="fixed inset-0 z-[70]" data-testid="tour" data-tour-id={id}>
      {rect ? (
        <div
          aria-hidden
          className="pointer-events-none fixed border-2 border-amber transition-all duration-200"
          style={{ ...rect, boxShadow: '0 0 0 9999px rgba(3, 5, 4, 0.72)' }}
        />
      ) : (
        <div aria-hidden className="fixed inset-0 bg-ink-950/75" />
      )}
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onKey}
        className="fixed flex flex-col gap-2 border border-amber bg-panel p-4 font-sans text-[13px] leading-relaxed text-fg shadow-2xl focus:outline-none"
        style={{ top: pos.top, left: pos.left, width: Math.min(CARD_W, window.innerWidth - 24) }}
      >
        <div className="flex items-baseline justify-between gap-2 font-mono text-[10px] tracking-[0.12em] text-dim uppercase">
          <span>Tutorial</span>
          <span>
            {index + 1}/{steps.length}
          </span>
        </div>
        <h2 id={titleId} className="font-mono text-[13px] font-bold tracking-[0.08em] text-amber uppercase">
          {step.title}
        </h2>
        {step.body.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
        <div className="mt-1 flex items-center gap-2 font-mono text-[11px] tracking-[0.06em] uppercase">
          <button type="button" className="text-muted hover:text-amber" onClick={close}>
            Pular
          </button>
          <span className="flex-1" />
          {index > 0 && (
            <button
              type="button"
              className="h-8 border border-line-2 px-3 text-fg-2 hover:border-amber hover:text-amber"
              onClick={() => setIndex(index - 1)}
            >
              Voltar
            </button>
          )}
          <button
            type="button"
            className="h-8 bg-amber px-3 font-bold text-ink-900 hover:bg-amber-2"
            onClick={() => (last ? close() : setIndex(index + 1))}
          >
            {last ? 'Entendi' : 'Próximo'}
          </button>
        </div>
        {index === 0 && replaying !== id && (
          <button
            type="button"
            className="self-start font-mono text-[10px] tracking-[0.06em] text-dim uppercase hover:text-amber"
            onClick={() => {
              setEnabled(false);
              close();
            }}
          >
            Não mostrar mais tutoriais
          </button>
        )}
      </div>
    </div>
  );
}
